<!-- Thread Input Component -->
<!-- Handles both empty state (with Bob's greeting) and regular input -->

<!-- Fullscreen drag-and-drop overlay (active when renderDragOverlay=true, e.g. new-chat page) -->
@if(renderDragOverlay() && isWindowDragActive()) {
<div @fadeOverlay
     class="fixed inset-0 z-[9999] flex items-center justify-center"
     style="background: rgba(255,255,255,0.85); backdrop-filter: blur(6px);"
     (dragover)="$event.preventDefault()">
    <div class="flex flex-col items-center gap-4 select-none pointer-events-none">
        <div class="w-20 h-20 rounded-full flex items-center justify-center" style="background: #FAF5EE;">
            <app-icon type="cloud-upload" source="lucide" size="xl" class="text-[#C8956B]"></app-icon>
        </div>
        <div class="text-center">
            <p class="text-lg font-semibold text-gray-900 leading-snug">Drop files here to add to chat</p>
            <p class="text-sm text-gray-400 mt-1">PDF, DOCX, XLSX, images and more up to 100 MB each</p>
        </div>
    </div>
</div>
}

<!-- ====================================== -->
<!-- EMPTY STATE: New Chat with Greeting -->
<!-- ====================================== -->
@if(showEmptyState()) {
<div class="greeting-container flex flex-col gap-4 lg:gap-6">
    <!-- Bob's Avatar and Greeting -->
    <div class="flex gap-3 lg:gap-4 items-center">
        <img [src]="'images/bob_agent/bob.svg' | assetUrl" class="bob-avatar flex-shrink-0" alt="Bob Agent Avatar"
            loading="lazy" />

        <div class="greeting-text theme-text flex flex-col font-bricolage font-semibold">
            <span>{{greeting()}}, {{currentUser()?.fullName}}.</span>
            <span>{{subtitle()}}</span>
        </div>
    </div>

    <!-- Mode Tabs + Input Container -->
    <div class="flex flex-col">
        @if(showModeTabs()) {
        <app-select-button [options]="availableModes()" [ngModel]="selectedMode()"
            (ngModelChange)="selectedMode.set($event)" class="block w-fit mb-1" [variant]="'rounded'" [size]="'small'">
        </app-select-button>
        }

        <!-- Input in Styled Container -->
        <ng-container *ngTemplateOutlet="inputCard"></ng-container>
    </div>

</div>
}

<!-- ====================================== -->
<!-- REGULAR INPUT: Active Thread -->
<!-- ====================================== -->
@else {
<div [class]="(threadId() && !threadMessagesService.messages().length) ? 'flex flex-col gap-1' : 'flex flex-col'">
    @if(threadId() && !threadMessagesService.messages().length) {
    <div class="flex gap-3 lg:gap-4 items-center">
        <img [src]="'images/bob_agent/bob.svg' | assetUrl" class="bob-avatar flex-shrink-0" alt="Bob Agent Avatar"
            loading="lazy" />
        <div class="greeting-text theme-text flex flex-col font-bricolage font-semibold">
            <span>{{greeting()}}, {{currentUser()?.fullName}}.</span>
            <span>{{subtitle()}}</span>
        </div>
    </div>
    }

    <!-- Mode Tabs -->
    @if(showModeTabs()) {
    <app-select-button [options]="availableModes()" [ngModel]="selectedMode()"
        (ngModelChange)="selectedMode.set($event)" class="block w-fit mb-1" [variant]="'rounded'" [size]="'small'">
    </app-select-button>
    }

    <ng-container *ngTemplateOutlet="inputCard"></ng-container>
</div>

<!-- AI Disclaimer Text -->
@if(!threadId() || threadMessagesService.messages().length) {
<div class="text-center text-[0.7rem] text-gray-400 font-medium tracking-[0.07px]">
    Bob AI is pretty smart, but hey, it's still AI. Check important info, just in case!
</div>
}
}

<!-- ====================================== -->
<!-- SHARED INPUT CARD TEMPLATE -->
<!-- ====================================== -->
<ng-template #inputCard>
    <div
        [class]="(agentNudgeVisible() || creditNudgeMode() === CREDIT_NUDGE_MODE.TO_AGENT || creditNudgeMode() === CREDIT_NUDGE_MODE.TO_ASK) ? 'p-2 rounded-base input-nudge-active' : ''">
        <!-- Agent Nudge -->
        @if(agentNudgeVisible()) {
        <div class="nudge-enter flex items-center justify-between gap-2 px-2 pb-1">
            <div class="flex items-center gap-2 min-w-0">
                <app-icon [type]="CREDITS_SVG.AGENT_MODE" source="svg" size="sm" />
                @if(agentModeHasCredits()) {
                <span class="text-gray-500 text-xs">This looks like an Agentic mode task. Switch to Agentic mode for
                    better results?<span class="ml-1 theme-text text-xs underline cursor-pointer"
                        (click)="switchToAgentMode()">Switch</span></span>
                } @else {
                <span class="text-gray-500 text-xs">This is an Agentic mode task. Add more credits to continue.</span>
                }
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
                <app-button variant="ghost" severity="contrast" [size]="'xxs'" icon="x" iconSource="lucide"
                    [iconSize]="'sm'" (clicked)="dismissNudge()"></app-button>
            </div>
        </div>
        }
        <!-- Credit Nudge: TO_AGENT -->
        @if(creditNudgeMode() === CREDIT_NUDGE_MODE.TO_AGENT) {
        <div class="nudge-enter flex items-center justify-between gap-2 px-2 pb-1">
            <div class="flex items-center gap-2 min-w-0">
                <app-icon [type]="CREDITS_SVG.AGENT_MODE" source="svg" size="sm" />
                <span class="text-gray-500 text-xs">You don't have enough credits in Ask mode. Switch to Agentic mode to
                    get results. <span class="theme-text text-xs underline cursor-pointer"
                        (click)="switchToAgentModeFromCredit()">Switch</span></span>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
                <app-button variant="ghost" severity="contrast" [size]="'xxs'" icon="x" iconSource="lucide"
                    [iconSize]="'sm'" (clicked)="dismissCreditNudge()"></app-button>
            </div>
        </div>
        }
        <!-- Credit Nudge: TO_ASK -->
        @if(creditNudgeMode() === CREDIT_NUDGE_MODE.TO_ASK) {
        <div class="nudge-enter flex items-center justify-between gap-2 px-2 pb-1">
            <div class="flex items-center gap-2 min-w-0">
                <app-icon type="zap" source="lucide" [size]="'sm'" class="text-amber-500 flex-shrink-0"></app-icon>
                <span class="text-gray-500 text-xs">You don't have enough credits in Agentic mode. Switch to Ask mode to
                    get results. <span class="theme-text text-xs underline cursor-pointer"
                        (click)="switchToAskMode()">Switch</span></span>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
                <app-button variant="ghost" severity="contrast" [size]="'xxs'" icon="x" iconSource="lucide"
                    [iconSize]="'sm'" (clicked)="dismissCreditNudge()"></app-button>
            </div>
        </div>
        }
        <div class="relative p-3 lg:p-4 bg-white rounded-2xl border border-gray-200 shadow-sm transition-all">
        
            <ng-container *ngTemplateOutlet="inputContent"></ng-container>
        </div>
    </div>
</ng-template>

<!-- ====================================== -->
<!-- SHARED INPUT CONTENT TEMPLATE -->
<!-- ====================================== -->
<ng-template #inputContent>
    <div class="flex flex-col gap-1.5 border-black">

        <!-- Voice Recording UI (shown when recording) -->
        @if(voiceService.isRecording() || isConfirmingVoice()) {
        <app-voice-recording-ui [audioLevel]="voiceService.audioLevel()" (cancel)="cancelVoiceRecording()"
            (confirm)="confirmVoiceRecording()">
        </app-voice-recording-ui>
        }

        <!-- Main Input (shown when not recording) -->
        @else {
        <div class="flex flex-col gap-3">
            <!-- Attachments Display -->
            @if(attachments().length > 0) {
            <app-attachment-group [attachments]="attachments()" [maxVisibleAttachments]="4" [horizontalScroll]="true"
                [canRemove]="!attachFilesLoading()"
                (removeAttachment)="removeAttachedFile($event)"></app-attachment-group>
            }

            <!-- Textarea -->
            <textarea #messageTextarea [(ngModel)]="messageText" [disabled]="disabled()"
                class="auto-grow-textarea w-full rounded-md border-none outline-none resize-none text-base text-justify text-gray-900 placeholder:text-gray-400 placeholder:italic leading-6 min-h-[3rem] max-h-[15rem] overflow-y-auto disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Describe what you need help with..." rows="2" (paste)="onPaste($event)">
            </textarea>

            <!-- Mobile: Selected Tools indicator (compact, shown only on mobile) -->
            @if(isMobile() && (getSelectedToolsOptions().length > 0 || customInstructions())) {
            <div class="flex items-center gap-1.5 flex-wrap lg:hidden">
                @for(option of getSelectedToolsOptions(); track option.value) {
                @if(option.value !== CHAT_STYLES.NORMAL) {
                <div class="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 cursor-pointer text-xs"
                    (click)="removeToolOption(option)">
                    <app-icon [type]="option.icon || ''" [size]="'sm'"
                        [source]="option.iconSource || IconSourceEnum.LUCIDE"></app-icon>
                    <span class="font-medium">{{ option.label }}</span>
                    <app-icon type="x" [size]="'sm'" source="lucide"></app-icon>
                </div>
                }
                }
                @if(customInstructions()) {
                <div class="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 cursor-pointer text-xs"
                    (click)="openEditCustomInstructions()">
                    <app-icon [type]="SVG_ICONS.INSTRUCTIONS" [source]="'svg'" [size]="'sm'"></app-icon>
                    <span class="font-medium">Instructions</span>
                    <app-icon type="x" [size]="'sm'" source="lucide"
                        (click)="customInstructions.set(''); $event.stopPropagation()"></app-icon>
                </div>
                }
            </div>
            }

            <!-- Actions Bar -->
            <div class="flex items-center justify-between gap-2">
                <!-- Left Side: Options, Tools, Connectors and Selected Tools -->
                <div class="flex items-center gap-1.5 flex-1 min-w-0">
                    <div class="flex items-center gap-1.5">
                        <!-- Options Menu (Attachments) - hidden when options feature is excluded -->
                        @if(isFeatureEnabled(THREAD_AGENT_FEATURES.OPTIONS)) {
                        <app-menu #optionsMenu [items]="optionsMenuItems()" (itemSelected)="onOptionSelect($event)"
                            (visibleChange)="isOptionsMenuOpen.set($event)"
                            [pTooltip]="disabled() ? 'Chat limit exceeded' : 'Options'" tooltipPosition="top">
                            <app-button [fullWidth]="true" [icon]="isOptionsMenuOpen() ? 'x' : 'plus'"
                                [iconSource]="'lucide'" [iconSize]="'sm'" variant="outlined" severity="contrast"
                                [disabled]="disabled() || attachFilesLoading()" (clicked)="openOptionsPanel($event)" />
                        </app-menu>
                        }

                        <!-- Tools Menu - hidden on mobile -->
                        <div class="hidden lg:block">
                            <app-menu #toolsMenu [items]="filteredToolsMenuItems()"
                                [selectedValues]="getSelectedToolsOptions()"
                                (itemSelected)="onToolsOptionSelect($event)" [pTooltip]="'Tools'" tooltipPosition="top">
                                <app-button [fullWidth]="true" [icon]="'settings-2'" [iconSource]="'lucide'"
                                    [iconSize]="'sm'" variant="outlined" severity="contrast"
                                    (clicked)="openToolsPanel($event)" />
                            </app-menu>
                        </div>

                        <!-- Connectors Menu (only if user has feature and connectors not excluded, and not in ASK mode) - hidden on mobile -->
                        @if(isFeatureEnabled(THREAD_AGENT_FEATURES.CONNECTORS) && hasIntegrationsFeature() &&
                        selectedMode() == AGENT_MODE.AGENTIC) {
                        <div class="hidden lg:block">
                            <app-menu #connectorsMenu [items]="connectorsMenuItems()"
                                [class]="'!max-h-[40vh] overflow-y-auto'"
                                [selectedValues]="getSelectedConnectorsOptions()" [search]="true"
                                (itemSelected)="onConnectorsOptionSelect($event)" [pTooltip]="'Connectors'"
                                tooltipPosition="top">
                                <app-button [fullWidth]="true" [icon]="'blocks'" [iconSource]="'lucide'"
                                    [iconSize]="'sm'" variant="outlined" severity="contrast"
                                    (clicked)="openConnectorsPanel($event)" />
                            </app-menu>
                        </div>
                        }

                        <!-- Mobile overflow menu: Tools, Connectors, Voice, Model - visible only on mobile -->
                        <div class="lg:hidden">
                            <app-menu #mobileOverflowMenu [items]="mobileOverflowMenuItems()"
                                (itemSelected)="onMobileOverflowSelect($event)" tooltipPosition="top">
                                <app-button [fullWidth]="true" [icon]="'ellipsis'" [iconSource]="'lucide'"
                                    [iconSize]="'sm'" variant="outlined" severity="contrast"
                                    (clicked)="mobileOverflowMenu.toggle($event)" />
                            </app-menu>
                        </div>
                    </div>

                    <!-- Selected Tools Display (inline with buttons) - hidden on mobile for space -->
                    <div class="hidden lg:flex items-start gap-1.5 overflow-x-auto">
                        @for(option of getSelectedToolsOptions(); track option.value) {
                        @if(option.value !== CHAT_STYLES.NORMAL) {
                        <div class="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 cursor-pointer min-w-fit"
                            (mouseenter)="hoveredOption = option" (mouseleave)="hoveredOption = null">

                            <div class="w-4 h-4 flex items-center justify-center">
                                <app-icon *ngIf="hoveredOption !== option" [type]="option.icon ?? ''" [size]="'sm'"
                                    [source]="option.iconSource || IconSourceEnum.LUCIDE">
                                </app-icon>

                                <app-icon *ngIf="hoveredOption === option" type="x" [size]="'sm'" source="lucide"
                                    (click)="removeToolOption(option); $event.stopPropagation()">
                                </app-icon>
                            </div>

                            <span class="text-sm font-medium">{{ option.label }}</span>
                        </div>
                        }
                        }

                        <!-- Custom Instructions Display -->
                        @if(customInstructions()) {
                        <div class="group flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 cursor-pointer min-w-fit"
                            (click)="openEditCustomInstructions()">

                            <div class="w-4 h-4 flex items-center justify-center">
                                <app-icon [type]="SVG_ICONS.INSTRUCTIONS" [source]="'svg'" [size]="'sm'"
                                    class="group-hover:hidden">
                                </app-icon>

                                <app-icon type="x" [source]="'lucide'" [size]="'sm'"
                                    (click)="customInstructions.set(''); $event.stopPropagation()"
                                    class="hidden group-hover:block">
                                </app-icon>
                            </div>

                            <span class="text-sm font-medium">Custom instructions</span>
                        </div>
                        }
                    </div>
                </div>

                <!-- Right Side: Model Selector, Voice, Send/Stop -->
                <div class="flex items-center gap-1.5">
                    <!-- Token Usage Pie Chart - hidden on mobile -->
                    @if(selectedModel() && threadMessagesService.tokenLimitsAgent() && maxTokens() > 0 &&
                    needTokenLimits()) {
                    <div class="hidden lg:flex items-center" [pTooltip]="getTooltipText()" tooltipPosition="top">
                        <svg width="16" height="16" viewBox="0 0 16 16" class="cursor-pointer">
                            <circle cx="8" cy="8" r="7"
                                [class]="tokenUsagePercentage() >= 100 ? 'token-chart-fill-gray-500' : 'token-chart-fill-gray-200'">
                            </circle>
                            @if(getPieChartPath()) {
                            <path [attr.d]="getPieChartPath()" class="token-chart-fill-gray-500"></path>
                            }
                        </svg>
                    </div>
                    }

                    <!-- Model Selector - icon-only on mobile, full on desktop -->
                    <app-model-selector [modelOptions]="modelOptions()" [(selectedModel)]="selectedProviderSignal"
                        [iconOnly]="isMobile()" (modelChanged)="onModelChange($event)">
                    </app-model-selector>

                    <!-- Voice Input Button - hidden on mobile -->
                    @if(voiceService.isSupported()) {
                    <app-button class="hidden lg:inline-flex" variant="outlined" severity="contrast" [icon]="'mic'"
                        [iconSource]="'lucide'" [iconSize]="'sm'" [loading]="isVoiceStarting()" pTooltip="Voice Input"
                        tooltipPosition="top" (clicked)="startVoiceRecording()">
                    </app-button>
                    }

                    <!-- Send or Stop Button - always visible -->
                    @if(creationLoading()) {
                    <!-- Loading State (Creating Thread) -->
                    <app-button variant="filled" severity="primary" [iconSize]="'sm'" [loading]="true"
                        pTooltip="Creating thread..." tooltipPosition="top" [enableThrottle]="true">
                    </app-button>
                    } @else if(!isLoadingOrSending()) {
                    <!-- Send Button -->
                    <app-button variant="filled" severity="primary" [icon]="'arrow-up'" [iconSource]="'lucide'"
                        [iconSize]="'sm'" [disabled]="disableSend()" [pTooltip]="sendButtonTooltip()"
                        tooltipPosition="top" (clicked)="sendMessage()" [enableThrottle]="true">
                    </app-button>
                    } @else {
                    <!-- Stop Button -->
                    <app-button variant="filled" severity="danger" [icon]="'square'" [iconSource]="'lucide'"
                        [iconSize]="'sm'" pTooltip="Stop" tooltipPosition="top" (clicked)="pauseThread()"
                        [enableThrottle]="true">
                    </app-button>
                    }
                </div>
            </div>
        </div>
        }
    </div>
</ng-template>

<!-- ====================================== -->
<!-- FILE SELECTION DIALOG -->
<!-- ====================================== -->
@if(openFileSelectionDialog()) {
<app-file-selection #fileSelectionDialog [(visible)]="openFileSelectionDialog" [config]="fileSelectionConfig()" [fetch]="fileSelectionFetch"
    [dropdownOptions]="fileSelectionDropdownOptions()" (onSubmit)="onFileSelectionSubmit($event)"
    (onCancel)="onFileSelectionCancel()">
</app-file-selection>
}

<!-- ====================================== -->
<!-- CUSTOM INSTRUCTIONS DIALOG -->
<!-- ====================================== -->
<app-dialog [visible]="openCustomInstructionsDialog()" (closed)="openCustomInstructionsDialog.set(false)"
    title="Custom instructions" [fullscreenOnMobile]="true" class="max-w-[40rem]">
    <ng-template #bodyTemplate>
        <textarea rows="4" pTextarea class="w-full" placeholder="Additional behavior, style, and tone preferences"
            [(ngModel)]="customInstructions"></textarea>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex items-end justify-end gap-1.5">
            <app-button variant="outlined" severity="contrast" label="Cancel"
                (clicked)="openCustomInstructionsDialog.set(false); customInstructions.set(_instructionsBeforeEdit);" />
            <app-button variant="filled" severity="primary" label="Save"
                (clicked)="openCustomInstructionsDialog.set(false)" />
        </div>
    </ng-template>
</app-dialog>

